import {
  Card,
  CardContent,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Skeleton,
  Button,
  Chip,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { formatCurrency } from "../constants";
import dayjs from "dayjs";

interface RecentOrdersTableProps {
  data: any[];
  loading: boolean;
}

const getStatusColor = (status: string): "success" | "error" | "warning" | "info" | "default" => {
  switch (status) {
    case "paid":
      return "success";
    case "failed":
    case "refused":
    case "rejected":
      return "error";
    case "pending_confirmation":
    case "unpaid":
      return "warning";
    case "refunded":
    case "partial_refund":
      return "info";
    default:
      return "default";
  }
};

const getDeliveryColor = (status: string): "success" | "error" | "warning" | "info" | "default" => {
  switch (status) {
    case "delivered":
      return "success";
    case "failed":
    case "delivery_failed":
      return "error";
    case "pending":
    case "in_progress":
      return "warning";
    default:
      return "default";
  }
};

const formatLabel = (s: string) =>
  s
    ?.replace(/_/g, " ")
    .replace(/\b\w/g, (l) => l.toUpperCase()) || "N/A";

const RecentOrdersTable = ({ data, loading }: RecentOrdersTableProps) => {
  const navigate = useNavigate();

  if (loading) {
    return (
      <Card sx={{ borderRadius: 2, boxShadow: "0 1px 3px 0 rgba(0,0,0,.1)" }}>
        <CardContent sx={{ p: 2.5 }}>
          <Skeleton width="30%" height={24} />
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} width="100%" height={48} sx={{ mt: 1 }} />
          ))}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ borderRadius: 2, boxShadow: "0 1px 3px 0 rgba(0,0,0,.1)" }}>
      <CardContent sx={{ p: 2.5 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            Recent Orders
          </Typography>
          <Button
            size="small"
            onClick={() => navigate("/orders")}
            sx={{ textTransform: "none", fontWeight: 500 }}
          >
            View All
          </Button>
        </Box>
        {data && data.length > 0 ? (
          <TableContainer sx={{ overflowX: "auto" }}>
            <Table size="small" sx={{ minWidth: 700 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Order #</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Customer</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: 12 }} align="right">Amount</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Payment</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Delivery</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.map((order: any) => (
                  <TableRow
                    key={order._id}
                    hover
                    sx={{ cursor: "pointer", "&:last-child td": { borderBottom: 0 } }}
                    onClick={() => navigate(`/orders/detail/${order.orderNumber}`)}
                  >
                    <TableCell sx={{ fontSize: 13, fontWeight: 500 }}>
                      {order.orderNumber}
                    </TableCell>
                    <TableCell sx={{ fontSize: 13 }}>
                      <Box>
                        <Typography variant="body2" sx={{ fontSize: 13, fontWeight: 500 }}>
                          {order.customerName?.trim() || "N/A"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
                          {order.customerEmail}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: 13, fontWeight: 600 }}>
                      {formatCurrency(order.totalAmount || 0)}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={formatLabel(order.paymentStatus)}
                        size="small"
                        color={getStatusColor(order.paymentStatus)}
                        variant="outlined"
                        sx={{ fontSize: 11, height: 22 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={formatLabel(order.deliveryStatus)}
                        size="small"
                        color={getDeliveryColor(order.deliveryStatus)}
                        variant="outlined"
                        sx={{ fontSize: 11, height: 22 }}
                      />
                    </TableCell>
                    <TableCell sx={{ fontSize: 12, color: "text.secondary" }}>
                      {dayjs(order.createdAt).format("DD MMM YYYY")}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Box sx={{ py: 4, textAlign: "center" }}>
            <Typography color="text.secondary">No recent orders</Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default RecentOrdersTable;
